import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "slide",
    "progress",
    "counter",
    "dotsContainer",
    "prevButton",
    "nextButton",
  ];
  static values = { index: { type: Number, default: 0 } };

  connect() {
    this.totalSlides = this.slideTargets.length;
    this.slugByIndex = this.slideTargets.map(
      (slide) => slide.dataset.slideSlug || null,
    );
    this.indexBySlug = new Map();
    this.slugByIndex.forEach((slug, i) => {
      if (slug && !this.indexBySlug.has(slug)) this.indexBySlug.set(slug, i);
    });

    this.buildDots();

    // Read initial slide from URL hash — accepts either a semantic slug
    // (#revenue, #forecast, …) or the legacy positional form (#slide-3).
    const initial = this.slideFromHash();
    this.showSlide(initial, false);

    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundHashChange = this.handleHashChange.bind(this);
    document.addEventListener("keydown", this.boundKeydown);
    window.addEventListener("hashchange", this.boundHashChange);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown);
    window.removeEventListener("hashchange", this.boundHashChange);
  }

  handleKeydown(event) {
    switch (event.key) {
      case "ArrowRight":
      case " ":
        event.preventDefault();
        this.next();
        break;
      case "ArrowLeft":
        event.preventDefault();
        this.previous();
        break;
      case "Escape":
        event.preventDefault();
        // Close any open dropdown first; only exit presentation if nothing was open
        const openMenu = this.element.querySelector(
          '[data-dropdown-target="menu"]:not(.hidden)',
        );
        if (openMenu) {
          openMenu.classList.add("hidden");
        } else {
          this.exit();
        }
        break;
    }
  }

  handleHashChange() {
    const index = this.slideFromHash();
    if (index !== this.indexValue) {
      this.showSlide(index, false);
    }
  }

  slideFromHash() {
    const raw = window.location.hash.replace(/^#/, "");
    if (!raw) return 0;

    // Legacy positional form: #slide-3 → index 2.
    const positional = raw.match(/^slide-(\d+)$/);
    if (positional) {
      const num = parseInt(positional[1], 10);
      if (num >= 1 && num <= this.totalSlides) return num - 1;
      return 0;
    }

    // Semantic form: #revenue, #forecast, … If the current deck doesn't
    // render a slide with that slug (e.g. comparing a report type that
    // omits the section) fall back to the first slide.
    if (this.indexBySlug && this.indexBySlug.has(raw)) {
      return this.indexBySlug.get(raw);
    }
    return 0;
  }

  next() {
    if (this.indexValue < this.totalSlides - 1) {
      this.indexValue++;
      this.showSlide(this.indexValue);
    }
  }

  previous() {
    if (this.indexValue > 0) {
      this.indexValue--;
      this.showSlide(this.indexValue);
    }
  }

  goToSlide(index) {
    this.showSlide(index);
  }

  openCompare(event) {
    event.preventDefault();
    const link = event.currentTarget;
    // Prefer the semantic slug so the sibling deck opens on the same
    // section even if its slide numbering differs. Fall back to the
    // positional form for slides that haven't been slugged yet.
    const slug = this.slugByIndex && this.slugByIndex[this.indexValue];
    const fragment = slug ? `#${slug}` : `#slide-${this.indexValue + 1}`;
    window.open(`${link.href}${fragment}`, "_blank");
  }

  exit() {
    const exitUrl = this.element.dataset.presentationExitUrl;
    if (exitUrl) {
      Turbo.visit(exitUrl);
    } else {
      history.back();
    }
  }

  buildDots() {
    if (!this.hasDotsContainerTarget) return;

    // Compact mode: with many slides, switch from per-slide dots to a thin
    // segmented bar that scales horizontally without overflowing the nav.
    this.compactDots = this.totalSlides > 12;

    if (this.compactDots) {
      this.dotsContainerTarget.classList.remove("gap-2");
      this.dotsContainerTarget.classList.add("gap-1", "max-w-[40vw]");
    }

    this.dots = [];
    for (let i = 0; i < this.totalSlides; i++) {
      const dot = document.createElement("button");
      const sizeClasses = this.compactDots
        ? ["h-1", "w-3"]
        : ["h-1.5", "w-1.5", "rounded-full"];
      dot.className = [
        ...sizeClasses,
        "bg-gray-300",
        "transition-all",
        "duration-300",
        "hover:bg-gray-500",
        "focus:outline-none",
        "focus:ring-2",
        "focus:ring-gray-400",
        "focus:ring-offset-1",
      ].join(" ");
      dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
      dot.addEventListener("click", () => this.goToSlide(i));
      this.dotsContainerTarget.appendChild(dot);
      this.dots.push(dot);
    }
  }

  showSlide(index, updateHash = true) {
    this.indexValue = index;

    this.slideTargets.forEach((slide, i) => {
      if (i === index) {
        slide.style.opacity = "1";
        slide.style.pointerEvents = "auto";
        slide.style.zIndex = "1";
      } else {
        slide.style.opacity = "0";
        slide.style.pointerEvents = "none";
        slide.style.zIndex = "0";
      }
    });

    // Update URL hash without triggering hashchange loop — prefer the
    // semantic slug so deep-links stay stable across report types.
    if (updateHash) {
      const slug = this.slugByIndex && this.slugByIndex[index];
      const fragment = slug ? `#${slug}` : `#slide-${index + 1}`;
      history.replaceState(null, "", fragment);
    }

    // Progress bar
    const progress = ((index + 1) / this.totalSlides) * 100;
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${progress}%`;
    }

    // Counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${index + 1} / ${this.totalSlides}`;
    }

    // Dots — visual state: active dot is gray-900, inactive is gray-300.
    // Size stays constant (h-1.5/w-1.5 in normal mode, h-1/w-3 in compact mode)
    // so the bottom nav doesn't reflow as the user advances.
    if (this.dots) {
      this.dots.forEach((dot, i) => {
        if (i === index) {
          dot.classList.remove("bg-gray-300");
          dot.classList.add("bg-gray-900");
        } else {
          dot.classList.remove("bg-gray-900");
          dot.classList.add("bg-gray-300");
        }
      });
    }

    // Prev/Next buttons enabled state
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = index === 0;
      this.prevButtonTarget.classList.toggle("opacity-30", index === 0);
      this.prevButtonTarget.classList.toggle("cursor-not-allowed", index === 0);
    }
    if (this.hasNextButtonTarget) {
      const atEnd = index === this.totalSlides - 1;
      this.nextButtonTarget.disabled = atEnd;
      this.nextButtonTarget.classList.toggle("opacity-30", atEnd);
      this.nextButtonTarget.classList.toggle("cursor-not-allowed", atEnd);
    }
  }
}
